import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, MapPin, Phone, Mail, Clock, ChevronRight } from 'lucide-react';
import { SITE_CONFIG } from '../data/mock';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-950 text-gray-300">
      {/* CTA Banner */}
      <div className="bg-green-600 py-10 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-2">
            Ready to Fuel Your Week?
          </h2>
          <p className="text-green-100 mb-6 text-base">
            Order by Thursday at 1 PM for Sunday delivery. Minimum 8 meals.
          </p>
          <Link
            to="/menu"
            className="inline-block bg-white text-green-700 font-bold px-8 py-3.5 rounded-xl hover:bg-green-50 transition-colors text-base shadow-lg"
          >
            View This Week's Menu →
          </Link>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img
                src="/logo.png"
                alt="DFW Fit Meals"
                className="h-10 w-auto object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden items-center gap-2">
                <div className="w-9 h-9 bg-green-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-extrabold text-lg">F</span>
                </div>
                <div className="flex flex-col leading-none">
                  <span className="font-extrabold text-white text-lg">Fit Pro</span>
                  <span className="font-semibold text-green-400 text-xs tracking-widest uppercase">Meals</span>
                </div>
              </div>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-5">
              Fresh, chef-crafted meal prep delivered every Sunday to the Dallas-Fort Worth metroplex. 
              Eat clean, save time, reach your goals.
            </p>
            <div className="flex gap-3">
              <a
                href={SITE_CONFIG.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-pink-600 hover:text-white transition-all"
                aria-label="Instagram"
              >
                <Instagram size={16} />
              </a>
              <a
                href={SITE_CONFIG.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-blue-600 hover:text-white transition-all"
                aria-label="Facebook"
              >
                <Facebook size={16} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-white mb-5 text-sm uppercase tracking-wider">Quick Links</h3>
            <ul className="space-y-2.5">
              {[
                { label: 'Home', href: '/' },
                { label: 'This Week\'s Menu', href: '/menu' },
                { label: 'How It Works', href: '/how-it-works' },
                { label: 'Delivery Areas', href: '/delivery' },
                { label: 'Meal Plan Builder', href: '/meal-plan' },
                { label: 'FAQ', href: '/faq' },
                { label: 'Rewards Program', href: '/rewards' },
                { label: 'Track Order', href: '/track' },
                { label: 'Leave a Review', href: '/reviews' },
                { label: 'Blog', href: '/blog' },
                { label: 'Affiliate Program', href: '/affiliate' },
                { label: 'Contact Us', href: '/contact' },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-green-400 text-sm transition-colors flex items-center gap-1 group"
                  >
                    <ChevronRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Order Info */}
          <div>
            <h3 className="font-bold text-white mb-5 text-sm uppercase tracking-wider">Order Info</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-sm text-gray-400">
                <Clock size={15} className="text-green-400 mt-0.5 shrink-0" />
                <span>Order Deadline: <span className="text-white font-medium">Thursday by 1 PM</span></span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-400">
                <Clock size={15} className="text-green-400 mt-0.5 shrink-0" />
                <span>Delivery: <span className="text-white font-medium">Sundays, early afternoon</span></span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-400">
                <ChevronRight size={15} className="text-green-400 mt-0.5 shrink-0" />
                <span>Minimum: <span className="text-white font-medium">8 meals</span></span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-400">
                <ChevronRight size={15} className="text-green-400 mt-0.5 shrink-0" />
                <span>Delivery fee: <span className="text-white font-medium">$15 flat</span></span>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-bold text-white mb-5 text-sm uppercase tracking-wider">Contact</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-sm text-gray-400">
                <MapPin size={15} className="text-green-400 mt-0.5 shrink-0" />
                <span>
                  Serving: <span className="text-white font-medium">{SITE_CONFIG.serviceArea}</span>
                </span>
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-400">
                <Phone size={15} className="text-green-400 shrink-0" />
                <a href={`tel:${SITE_CONFIG.phone}`} className="hover:text-green-400 transition-colors">
                  {SITE_CONFIG.phone}
                </a>
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-400">
                <Mail size={15} className="text-green-400 shrink-0" />
                <a href={`mailto:${SITE_CONFIG.email}`} className="hover:text-green-400 transition-colors">
                  {SITE_CONFIG.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
          <p>© {currentYear} {SITE_CONFIG.name}. All rights reserved.</p>
          <p>Proudly serving the DFW metroplex 🏈</p>
        </div>
      </div>

      {/* Local Business Schema Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": ["LocalBusiness", "FoodEstablishment"],
                "@id": "https://dfwfitmeals.com/#business",
                "name": "Fit Pro Meals",
                "url": "https://dfwfitmeals.com/",
                "logo": SITE_CONFIG.logoUrl,
                "description": "Chef-crafted, macro-tracked meal prep delivered every Sunday across the Dallas–Fort Worth metroplex. Never frozen. Minimum 8 meals, $15 flat delivery.",
                "telephone": "+12148761653",
                "email": SITE_CONFIG.email,
                "priceRange": "$$",
                "servesCuisine": ["Healthy", "American", "Meal Prep"],
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "Fort Worth",
                  "addressRegion": "TX",
                  "addressCountry": "US"
                },
                "areaServed": [
                  { "@type": "City", "name": "Fort Worth" },
                  { "@type": "City", "name": "Dallas" },
                  { "@type": "City", "name": "Plano" },
                  { "@type": "City", "name": "Frisco" },
                  { "@type": "City", "name": "Arlington" },
                  { "@type": "City", "name": "Irving" },
                  { "@type": "City", "name": "Garland" },
                  { "@type": "City", "name": "Grand Prairie" }
                ],
                "hasOfferCatalog": {
                  "@type": "OfferCatalog",
                  "name": "Weekly Meal Prep Menu",
                  "url": "https://dfwfitmeals.com/menu"
                },
                "sameAs": [
                  SITE_CONFIG.instagram,
                  SITE_CONFIG.facebook
                ]
              },
              {
                "@type": "WebSite",
                "@id": "https://dfwfitmeals.com/#website",
                "url": "https://dfwfitmeals.com/",
                "name": "Fit Pro Meals",
                "publisher": { "@id": "https://dfwfitmeals.com/#business" }
              }
            ]
          }),
        }}
      />
    </footer>
  );
}
